import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { formattedDate } from '@/utils/time';

import Table from '@/components/Table';
import useSettlementQuery from '@/hooks/settlement/useSettlementQuery';
import { Order } from '@/apis/order/interface';
import _ from 'lodash';
import { formatToIconOrderType } from '@/utils/tableFormat';
import { formatPhoneDefault } from '@/utils/form';
import SearchFilter from './containers/SearchFilter';
import Settlements from './containers/Settlements';
import DeliveryDetail from './containers/DeliveryDetail';
import moment from 'moment';
import { ORDER_LIST_TABS, PageInfomation, SettlementDetails, SettlementFilters } from '@/apis/settlement/interface';
import TextComponent from '@/components/TextComponent';
import { ReactComponent as Question } from '@/assets/images/layout/question.svg';
import Tooltip from '@/components/Tooltip';
import OrderTabs from './containers/OrderTabs';
import { SortType } from '@/api/bff_types';
import settlementService from '@/services/settlements';
import theme from '@/styles/theme';

const HistoryOrdersPage = () => {
  const history = useHistory();
  const { settlementListQuery } = useSettlementQuery();

  const [filter, setFilter] = useState<SettlementFilters>({
    from: moment().subtract(1, 'M').format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD')
  });

  const [pharmacyPageInfo, setPharmacyPageInfo] = useState<PageInfomation>({
    page: 1,
    limit: 10
  });
  const [variancePageInfo, setVariancePageInfo] = useState<PageInfomation>({
    page: 1,
    limit: 10
  });
  const [variancePivotId, setVariancePivotId] = useState<number>();

  const headerFilter = { ...filter, ...pharmacyPageInfo };

  const [selectedTab, setSelectedTab] = useState<ORDER_LIST_TABS>(
    ORDER_LIST_TABS.PHARMACY.tabKey
  );

  const TABS = Object.values(ORDER_LIST_TABS);

  const onSelectTab = (selectTab: ORDER_LIST_TABS) => {
    setSelectedTab(selectTab);
  };

  const { data: settlementData } = settlementListQuery({
    ...filter,
    ...pharmacyPageInfo
  });

  const { costReimbursementSummary } =
    settlementService.getVarianceSummaryByPharmacyService({
      createdAtFrom: formattedDate(filter.from, 'x'),
      createdAtTo: formattedDate(filter.to, 'x')
    });
  const { costReimbursements } = settlementService.getVarianceByPharmacyService(
    {
      createdAtFrom: formattedDate(filter.from, 'x'),
      createdAtTo: formattedDate(filter.to, 'x')
    },
    {
      size: variancePageInfo.limit,
      sort: SortType.ASC,
      pivotId: variancePivotId
    }
  );

  useEffect(() => {
    // 첫페이지에서는 pivotId가 존재하지 않음
    if (variancePageInfo.page === 1) return setVariancePivotId(undefined);

    const costReimbursementsLength = costReimbursements.length;

    if (costReimbursementsLength === 0) return;

    setVariancePivotId(
      costReimbursements[costReimbursementsLength - 1].costReimbursementId
    );
  }, [variancePageInfo.page, filter.from, filter.to]);

  const PaymentsHeaderName = () => (
    <HeaderNameWithIcon>
      <HeaderName>결제액</HeaderName>
      <QuestionWrapper>
        <a data-tip data-for="paymentsHeader">
          <Question width={18} height={18} />
        </a>
        <Tooltip id="paymentsHeader" place="top" type="info" effect="solid">
          <TextComponent color={'#fff'}>환자 본인 부담금입니다.</TextComponent>
        </Tooltip>
      </QuestionWrapper>
    </HeaderNameWithIcon>
  );

  const DaysHeaderName = ({ tab }: { tab: ORDER_LIST_TABS }) => {
    const selectedPharmacyTab = tab === ORDER_LIST_TABS.PHARMACY.tabKey;
    return (
      <HeaderNameWithIcon>
        <HeaderName>{selectedPharmacyTab ? '일자' : '등록 일자'}</HeaderName>
        <QuestionWrapper>
          <a data-tip data-for={selectedPharmacyTab ? 'doneAt' : 'createdAt'}>
            <Question width={18} height={18} />
          </a>
          <Tooltip
            id={selectedPharmacyTab ? 'doneAt' : 'createdAt'}
            place="top"
            type="info"
            effect="solid">
            <TextComponent color={'#fff'}>
              {selectedPharmacyTab
                ? '일자는 결제 완료 기준입니다.'
                : '변동비용 정산 기준입니다.'}
            </TextComponent>
          </Tooltip>
        </QuestionWrapper>
      </HeaderNameWithIcon>
    );
  };
  //Table props
  const pharmacyHeaders = [
    {
      name: <DaysHeaderName tab={ORDER_LIST_TABS.PHARMACY.tabKey} />,
      flex: 2.5,
      alias: 'doneAt'
    },
    { name: '수령방법', flex: 2, alias: 'orderType' },
    { name: '환자명', flex: 2, alias: 'username' },
    { name: '전화번호', flex: 3, alias: 'phone' },
    { name: '주문 상태', flex: 2, alias: 'payments' },
    { name: <PaymentsHeaderName />, flex: 2, alias: 'price' },
    { name: '-', flex: 0.5, alias: '' },
    { name: '카드 수수료', flex: 2, alias: 'fee' },
    { name: '=', flex: 0.5, alias: '' },
    { name: '정산 예정 금액', flex: 3, alias: 'settlements' }
  ];
  const varianceHeaders = [
    {
      name: <DaysHeaderName tab={ORDER_LIST_TABS.VARIANCE.tabKey} />,
      flex: 2.5,
      alias: 'createdAt'
    },
    { name: '환자명', flex: 2, alias: 'patientName' },
    { name: '변동내역', flex: 3, alias: 'varianceReason' },
    { name: '변동금액', flex: 2, alias: 'varianceAmount' },
    { name: '-', flex: 0.5, alias: '' },
    { name: '카드 수수료', flex: 2, alias: 'cardCommission' },
    { name: '=', flex: 0.5, alias: '' },
    { name: '총 변동금액', flex: 2, alias: 'totalAmount' },
    { name: '진료정보', flex: 4, alias: 'orderExternalId' }
  ];

  const rowDataOrderFormat = (item: SettlementDetails, alias: string) => {
    switch (alias) {
      case 'doneAt':
        return formattedDate(item.doneAt!, 'YYYY-MM-DD');
      case 'createdAt':
        return formattedDate(item.createdAt!, 'YYYY-MM-DD');
      case 'orderType':
        return formatToIconOrderType(item.orderType, item.agencyType);
      case 'username':
        return item.patientName;
      case 'phone':
        return formatPhoneDefault(item.phone);
      case 'payments':
        return !!item.doneAt ? '완료' : '취소';
      case 'price':
        return `${item.price}원`;
      case 'fee':
        return `${Math.ceil(item.fee)}원`;
      case 'settlements':
        return `${item.price - Math.ceil(item.fee)}원`;
    }
  };

  const goDetailPage = (id: number, item?: Order) => {
    if (!_.isEmpty(item)) {
      if (!item?.orderId) {
        return;
      }
      history.push(`/history/${item?.orderId}`);
    }
  };

  const onChangePage = (tab: ORDER_LIST_TABS) => (value: number) => {
    tab === 'pharmacy'
      ? setPharmacyPageInfo({
          ...pharmacyPageInfo,
          page: value
        })
      : setVariancePageInfo({
          ...variancePageInfo,
          page: value
        });
  };
  const onChangeLimit = (tab: ORDER_LIST_TABS) => (value: number) => {
    tab === 'pharmacy'
      ? setPharmacyPageInfo({
          ...pharmacyPageInfo,
          limit: value,
          page: 1
        })
      : setVariancePageInfo({
          ...variancePageInfo,
          limit: value,
          page: 1
        });
  };

  return (
    <Wrapper>
      <Top>
        <SearchFilter filter={filter} setFilter={setFilter} />
        <Settlements
          filter={filter}
          reimbursementSum={costReimbursementSummary.reimbursementSum}
          reimbursementSumWon={costReimbursementSummary.reimbursementSumWon}
        />
        <DeliveryDetail filter={headerFilter} />
      </Top>
      <TableWrapper>
        <OrderTabs
          tabs={TABS}
          selectedTab={selectedTab}
          onSelectTab={onSelectTab}
        />
        {selectedTab === ORDER_LIST_TABS.PHARMACY.tabKey && (
          <Table
            tableKey="settle"
            tableHeader={' '}
            data={settlementData?.settlementDetails || []}
            headers={pharmacyHeaders}
            rowDataFormat={rowDataOrderFormat}
            page={pharmacyPageInfo.page}
            totalCount={settlementData?.pagination.totalCount || 0}
            setPage={onChangePage(ORDER_LIST_TABS.PHARMACY.tabKey)}
            itemsCountPerPage={pharmacyPageInfo.limit}
            onClick={goDetailPage}
            limitFilter={onChangeLimit(ORDER_LIST_TABS.PHARMACY.tabKey)}
          />
        )}
        {selectedTab === ORDER_LIST_TABS.VARIANCE.tabKey && (
          <Table
            tableKey="variance"
            tableHeader={' '}
            data={costReimbursements || []}
            headers={varianceHeaders}
            page={variancePageInfo.page}
            totalCount={costReimbursementSummary?.count || 0}
            setPage={onChangePage(ORDER_LIST_TABS.VARIANCE.tabKey)}
            itemsCountPerPage={variancePageInfo.limit}
            onClick={goDetailPage}
            limitFilter={onChangeLimit(ORDER_LIST_TABS.VARIANCE.tabKey)}
          />
        )}
      </TableWrapper>
    </Wrapper>
  );
};

export default HistoryOrdersPage;

const Wrapper = styled.div`
  width: 100%;

  ${({ theme }) => theme.media.threeQuarter`
    width: 100%;
  `};
`;
const Top = styled.div`
  display: flex;
  margin-bottom: 32px;

  > * {
    &:nth-child(1) {
      margin-right: 12px;
    }
    &:nth-child(2) {
      margin-right: 12px;
    }
  }
`;

const HeaderNameWithIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderName = styled(TextComponent)`
  color: ${theme.utils.withOpacity(theme.DF_COLOR.BLACK, 0.4)};
  font-size: 15px;
  font-weight: 500;
  margin-right: 5px;
`;

const QuestionWrapper = styled.div`
  height: 18px;
`;

const TableWrapper = styled.div`
  position: relative;
`;
